import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import DataTable from 'components/Table/DataTable';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import PauseIcon from '@material-ui/icons/Pause';
import CheckIcon from '@material-ui/icons/Check';
import RemoveShoppingCartIcon from '@material-ui/icons/RemoveShoppingCart';
import Tooltip from '@material-ui/core/Tooltip';
import NestedTableContainer from 'components/Table/NestedTableContainer';
import OrderTable from 'components/OrderTable';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  actionButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
    margin: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
  actionButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statusIcon: {
    marginRight: theme.spacing(1),
  },
}));

function Elders({
  elders,
  onOrdersAccept,
  onOrdersAbandon,
}) {
  const classes = useStyles();

  if (!elders || elders.length === 0) {
    return <></>;
  }

  return (
    <React.Fragment>
      {elders.map((elder, index) => {
        const elderName = elder.name;
        const elderMealCount = elder.mealCount;
        const elderGroupPurchaseStatus = elder.groupPurchaseStatus;
        const elderOrderId = elder.orderId;
        return <div key={`${elder.id}-${index}`} className={classes.actionButtonContainer}>
          {elderGroupPurchaseStatus === 'accepted' &&
          <Tooltip
            title={'已接單'}
            placement="bottom"
          >
            <CheckIcon style={{ color: 'green' }} className={classes.statusIcon} />
          </Tooltip>}
          {elderGroupPurchaseStatus === 'abandoned' &&
          <Tooltip
            title={'已拒單'}
            placement="bottom"
          >
            <CloseIcon style={{ color: 'red' }} className={classes.statusIcon} />
          </Tooltip>}
          {elderGroupPurchaseStatus === 'pending' &&
          <Tooltip
            title={'未接單'}
            placement="bottom"
          >
            <PauseIcon style={{ color: 'orange' }} className={classes.statusIcon} />
          </Tooltip>}
          {elderGroupPurchaseStatus === 'clientCancelled' &&
          <Tooltip
            title={'客戶退單'}
            placement="bottom"
          >
            <RemoveShoppingCartIcon style={{ color: 'grey' }} className={classes.statusIcon} />
          </Tooltip>}
          <span>{elderName} ({elderMealCount})</span>
          {elderGroupPurchaseStatus !== 'accepted' && elderGroupPurchaseStatus !== 'clientCancelled' &&
          <Button
            size="small"
            variant="contained"
            color="primary"
            className={classes.actionButton}
            onClick={() => {
              onOrdersAccept && onOrdersAccept({ orderIds: [elderOrderId] });
            }}
          >
            接單
          </Button>}
          {elderGroupPurchaseStatus !== 'abandoned' && elderGroupPurchaseStatus !== 'clientCancelled' &&
          <Button
            size="small"
            variant="contained"
            color="secondary"
            className={classes.actionButton}
            onClick={() => {
              onOrdersAbandon && onOrdersAbandon({ orderIds: [elderOrderId] });
            }}
          >
            拒單
          </Button>}
        </div>;
      })}
    </React.Fragment>);
}

Elders.propTypes = {
  elders: PropTypes.array,
  onOrdersAccept: PropTypes.func,
  onOrdersAbandon: PropTypes.func,
};

const defaultColumns = [
  {
    name: 'index',
    label: ' ',
    options: {
      display: 'excluded',
      filter: false,
      sort: false,
    },
  },
  {
    name: 'restaurantName',
    label: '餐廳',
    options: {
      display: true,
      filter: true,
      sort: true,
    },
  },
  {
    name: 'mealItemName',
    label: '餐點',
    options: {
      display: true,
      filter: true,
      sort: true,
    },
  },
];

export default function GroupPurchasesTable({
  description,
  data: inData,
  onOrdersAccept,
  onOrdersAbandon,
  onRefresh,
  title = '',
  ...props
}) {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState(defaultColumns);
  const [filterList, setFilterList] = useState([]);
  const parentRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState('100%');
  const resizeObserverRef = useRef(null);

  const setupResizeObserver = useCallback((element) => {
    if (element && !resizeObserverRef.current) {
      const updateWidth = () => {
        if (element && element.offsetWidth > 0) {
          setContainerWidth(`${element.offsetWidth}px`);
        }
      };

      resizeObserverRef.current = new ResizeObserver(updateWidth);
      resizeObserverRef.current.observe(element);
      updateWidth();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  const actionButtons = {
    name: 'index',
    label: ' ',
    options: {
      display: 'always',
      filter: false,
      sort: false,
      download: false,
      print: false,
      customBodyRender(_, tableMeta) {
        return (
          <div className={classes.actionButtonContainer}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.actionButton}
              onClick={() => {
                onOrdersAccept && onOrdersAccept({ orderIds: inData[tableMeta.rowData[0]].orderIds || [] });
              }}
            >
              全部接單
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              className={classes.actionButton}
              onClick={() => {
                onOrdersAbandon && onOrdersAbandon({ orderIds: inData[tableMeta.rowData[0]].orderIds || [] });
              }}
            >
              全部拒單
            </Button>
          </div>
        );
      },
    },
  };

  const options = {
    expandableRows: true,
    isRowExpandable: () => true,
    onTableChange: (action, tableState) => {
      if (action === 'filterChange') {
        setFilterList(tableState.filterList);
      }
    },
    renderExpandableRow(_, rowMeta) {
      const orderIds = inData[rowMeta.dataIndex]?.orderIds || [];
      if (orderIds.length === 0) {
        return <></>;
      }
      return (
        <NestedTableContainer columns={columns}>
          <div
            ref={(el) => {
              parentRef.current = el;
              setupResizeObserver(el);
            }}
            style={{ width: '100%' }}
          >
            <Container maxWidth={false} style={{ width: containerWidth }}>
              <OrderTable title='團購餐點訂單' orderIds={orderIds} />
            </Container>
          </div>
        </NestedTableContainer>
      );
    },
  };

  useEffect(() => {
    const newData = [];
    const newColumns = [...defaultColumns];
    const restaurantFilter = filterList[1] || [];
    const mealFilter = filterList[2] || [];

    const total = inData.reduce((sum, row) => {
      let needToCount = true;
      if (restaurantFilter.length !== 0 && !restaurantFilter.includes(row.restaurantName)) {
        needToCount = false;
      }
      if (mealFilter.length !== 0 && !mealFilter.includes(row.mealItemName)) {
        needToCount = false;
      }
      if (needToCount) {
        return sum + row.total || 0;
      } else {
        return sum;
      }
    }, 0);

    if (inData?.length !== 0) {
      newColumns.push({
        name: 'total',
        label: `總餐數 (${total})`,
        options: {
          display: 'always',
          filter: false,
          sort: true,
        },
      });
      inData[0].deliveryStaffMealCountList.forEach(({ id, name }) => {
        const deliveryStaffTotal = inData.reduce((sum, row) => {
          let needToCount = true;
          if (restaurantFilter.length !== 0 && !restaurantFilter.includes(row.restaurantName)) {
            needToCount = false;
          }
          if (mealFilter.length !== 0 && !mealFilter.includes(row.mealItemName)) {
            needToCount = false;
          }
          if (needToCount) {
            return sum + (row.deliveryStaffMealCountList.find((item) => item.id === id) || {}).count || 0;
          } else {
            return sum;
          }
        }, 0);
        newColumns.push({
          name: id,
          label: `${name} (${deliveryStaffTotal})`,
          options: {
            display: 'always',
            filter: false,
            sort: false,
            customBodyRender: (value, tableMeta) => {
              return (
                <Elders
                  elders={value.elders}
                  onOrdersAccept={onOrdersAccept}
                  onOrdersAbandon={onOrdersAbandon}
                />
              );
            },
          },
        });
      });
      inData.forEach(({ index, restaurantName, mealItemName, deliveryStaffMealCountList, total }) => {
        const row = {
          index,
          restaurantName,
          mealItemName,
          total,
        };
        deliveryStaffMealCountList.forEach(({ id, count, elders, orders }) => {
          Object.assign(row, { [id]: { count, elders, orders } });
        });
        newData.push(row);
      });
    }
    setData(newData);
    setColumns(newColumns);
    setColumns([...newColumns, actionButtons]);
  }, [inData, filterList]);

  const customDownloadData = (columns, data) => {
    const isUUID = (str) => {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(str);
    };
    columns.forEach(({ name }, index) => {
      if (isUUID(name) || name === 'N/A') { // 'N/A' 是未指定送餐大使
        data.forEach((value) => {
          if (!value.data[index].elders || value.data[index].elders.length === 0) {
            value.data[index] = value.data[index].count;
          } else {
            value.data[index] = value.data[index].count + '\n' +
              value.data[index].elders.map((elder) => {
                return elder.name;
              }).filter((value) => value !== undefined).join('\n');
          }
        });
      }
    });
    return data;
  };

  return (
    <DataTable
      data={data}
      title={title}
      description={description}
      columns={columns}
      options={options}
      defaultIndex={0}
      indexes={[]}
      queryDefaultParams={{}}
      onRefresh={onRefresh}
      customDownloadData={customDownloadData}
      {...props}
    />
  );
}

GroupPurchasesTable.propTypes = {
  description: PropTypes.string,
  data: PropTypes.array,
  onOrdersAccept: PropTypes.func,
  onOrdersAbandon: PropTypes.func,
  onRefresh: PropTypes.func,
  title: PropTypes.string,
};
